import { useParams, Navigate } from "react-router-dom";
import { newsItems } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import NewsBreadcrumb from "./components/NewsBreadcrumb";
import ArticleStructuredData from "./components/ArticleStructuredData";
import FaqSection from "@/components/feature/FaqSection";

const newsFaq = [
  {
    question: "GRANSTARKはどんな警備会社ですか？",
    answer:
      "株式会社GRANSTARKは2019年創業の大阪密着の警備会社です。大阪府公安委員会認定（第62003216号）を取得しており、交通誘導・高速道路・イベント（雑踏）・施設警備の4分野に対応しています。",
  },
  {
    question: "GRANSTARKに警備を依頼するにはどうすればいいですか？",
    answer:
      "お電話（06-6786-9252）またはホームページのお問い合わせフォームからご連絡ください。最短即日でご対応いたします。",
  },
];

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = newsItems.find((item) => item.slug === slug);
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  useSeo({
    title: article ? `${article.title}｜株式会社GRANSTARK` : "ニュース｜株式会社GRANSTARK",
    description: article?.excerpt || "GRANSTARKの最新情報・お知らせ一覧ページです。",
    ogUrl: article
      ? `https://www.gs-granstark.com/news/${article.slug}`
      : "https://www.gs-granstark.com/news",
    ogTitle: article ? `${article.title}｜株式会社GRANSTARK` : "ニュース｜株式会社GRANSTARK",
    ogDescription: article?.excerpt || "GRANSTARKの最新情報・お知らせ一覧ページです。",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `https://www.gs-granstark.com/news/${article.slug}#webpage`,
            url: `https://www.gs-granstark.com/news/${article.slug}`,
            name: `${article.title}｜株式会社GRANSTARK`,
            description: article.excerpt,
            isPartOf: { "@id": "https://www.gs-granstark.com/#website" },
            publisher: { "@id": "https://www.gs-granstark.com/#business" },
            mainEntity: { "@id": `https://www.gs-granstark.com/news/${article.slug}#article` },
            inLanguage: "ja",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: newsFaq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          },
        ]
      : [],
  });

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  const paragraphs = article.content.split("\n\n");

  return (
    <div>
      <ArticleStructuredData article={article} />

      <NewsBreadcrumb articleTitle={article.title} />

      <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: `url('${article.imageUrl}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/60 to-navy/80 pointer-events-none" />
        <div className="relative z-10 h-full flex items-end pb-8 md:pb-12">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-gold text-sm font-bold tracking-wide">
                {article.date}
              </span>
              <span className="w-px h-4 bg-white/40" />
              <span className="text-white/70 text-sm">{article.category}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </section>

      <article className="bg-white py-12 md:py-16">
        <div
          ref={ref}
          className={`w-full px-4 md:px-8 max-w-4xl mx-auto transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50">
              <i className="ri-newspaper-line text-xl text-navy" />
            </div>
            <div>
              <span className="text-gold text-sm font-bold tracking-wide block">
                {article.date}
              </span>
              <span className="text-gray-400 text-xs">{article.category}</span>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            {paragraphs.map((paragraph, i) => (
              <p
                key={i}
                className="text-gray-700 leading-[1.9] mb-6 text-[15px] md:text-base"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100">
            <a
              href="/news"
              className="inline-flex items-center gap-2 text-navy hover:text-gold transition-colors duration-300 text-sm font-medium"
            >
              <i className="ri-arrow-left-s-line text-lg" />
              ニュース一覧へ戻る
            </a>
          </div>
        </div>
      </article>

      <FaqSection items={newsFaq} bgColor="bg-gray-50" />
    </div>
  );
}
